import React, { useCallback, useMemo, useRef, useState } from "react";
import { Icon } from "@fluentui/react";

// ─────────────────────────────────────────────────────────────────────────────
// DataTable — finance-grade tabular component used across the Finance &
// Contract Expenditure dashboard. Refined enterprise aesthetic (Segoe UI,
// Boroondara green, tabular numerals, sticky header). Designed to make a
// 3,000-row PO list as scannable as a 30-row cost-centre roll-up:
//
//   - In-line magnitude bars behind currency columns so the eye reads
//     relative size before reading the number
//   - Per-column filter (typed value) with a green underline when active
//   - Click header to sort asc / desc / off (tri-state)
//   - Sticky header with a soft shadow on scroll
//   - Density toggle (compact 32px / comfortable 40px) in the footer
//   - One-click CSV export of the *currently filtered + sorted* rows
//   - "X of Y rows" with a one-click clear-filters action
//   - Optional onRowClick (opens the search Entity 360 drawer)
//
// Stays inside the immutable Boroondara branding — no Fluent table primitives
// because they don't give us enough control over information density.
// ─────────────────────────────────────────────────────────────────────────────

export type ColumnFormat = "currency" | "percent" | "number" | "date" | "text";

export interface Column<T> {
  key: string;
  label: string;
  align?: "left" | "right";
  format?: ColumnFormat;
  showBar?: boolean;            // inline magnitude bar (currency / number only)
  render?: (row: T) => React.ReactNode;
  // Override the substring filter. Returns true when row passes the term.
  filterFn?: (row: T, term: string) => boolean;
  // Min width hint
  minWidth?: number;
  // Show this column's filter input? Defaults to true.
  filterable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  // Default density.
  density?: "compact" | "comfortable";
  // Default sort, e.g. { key: "TotalSpend", dir: "desc" }
  defaultSort?: { key: string; dir: "asc" | "desc" };
  // File name (without .csv extension) for the export button. Pass null to
  // hide the button entirely.
  exportFileName?: string | null;
  // Row click → open profile. Wired by callers to the search EntityDrawer.
  onRowClick?: (row: T) => void;
  // Whether to show "X of Y rows" footer (default true)
  showFooter?: boolean;
  // Cap initial render — Show all (N) reveals the rest. Default 200; pass
  // null for no cap.
  initialRowLimit?: number | null;
  // Empty-state copy
  emptyMessage?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────
function fmtCurrency(v: any): string {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return "$" + Math.round(n).toLocaleString("en-AU");
}
function fmtPercent(v: any): string {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toFixed(1) + "%";
}
function fmtNumber(v: any): string {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-AU");
}
function fmtDate(v: any): string {
  if (!v) return "—";
  const s = String(v);
  return s.length > 10 ? s.slice(0, 10) : s;
}
function fmtCell(val: any, col: Column<any>): string {
  if (val == null || val === "") return "—";
  switch (col.format) {
    case "currency": return fmtCurrency(val);
    case "percent":  return fmtPercent(val);
    case "number":   return fmtNumber(val);
    case "date":     return fmtDate(val);
    default:         return String(val);
  }
}
function csvEscape(v: any): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}
function downloadCSV(filename: string, columns: Column<any>[], rows: any[]) {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(r[c.key] ?? "")).join(","))
    .join("\r\n");
  const blob = new Blob([header + "\r\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Component ────────────────────────────────────────────────────────────
export default function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  density: initialDensity = "compact",
  defaultSort,
  exportFileName = "export",
  onRowClick,
  showFooter = true,
  initialRowLimit = 200,
  emptyMessage = "No rows match the current filters.",
}: DataTableProps<T>) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(defaultSort || null);
  const [density, setDensity] = useState<"compact" | "comfortable">(initialDensity);
  const [showAll, setShowAll] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // ── Filtering ──
  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v && v.trim().length > 0);
    if (active.length === 0) return rows;
    return rows.filter((row) =>
      active.every(([key, term]) => {
        const col = columns.find((c) => c.key === key);
        if (!col) return true;
        if (col.filterFn) return col.filterFn(row, term);
        const cell = row[key];
        if (cell == null) return false;
        return String(cell).toLowerCase().includes(term.toLowerCase().trim());
      })
    );
  }, [rows, filters, columns]);

  // ── Sorting ──
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const isNumeric = col.format === "currency" || col.format === "percent" || col.format === "number";
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (isNumeric) {
        const na = Number(av), nb = Number(bv);
        if (na < nb) return -1 * dir;
        if (na > nb) return  1 * dir;
        return 0;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sort, columns]);

  // ── Per-column max for inline magnitude bars ──
  const columnMax = useMemo(() => {
    const out: Record<string, number> = {};
    for (const col of columns) {
      if (!col.showBar) continue;
      let max = 0;
      for (const r of sorted) {
        const v = Math.abs(Number(r[col.key]) || 0);
        if (v > max) max = v;
      }
      out[col.key] = max;
    }
    return out;
  }, [sorted, columns]);

  // ── Visible slice ──
  const visible = useMemo(() => {
    if (initialRowLimit == null || showAll) return sorted;
    return sorted.slice(0, initialRowLimit);
  }, [sorted, showAll, initialRowLimit]);

  // ── Handlers ──
  const toggleSort = useCallback((key: string) => {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: "desc" };
      if (cur.dir === "desc") return { key, dir: "asc" };
      return null; // tri-state: desc → asc → off
    });
  }, []);
  const onFilterChange = (key: string, val: string) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setShowAll(false); // reset paging when filter changes
  };
  const clearFilters = () => { setFilters({}); setShowAll(false); };
  const activeFilterCount = Object.values(filters).filter((v) => v && v.trim()).length;
  const onScroll = () => {
    if (scrollerRef.current) setScrolled(scrollerRef.current.scrollTop > 1);
  };
  const doExport = () => {
    const name = (exportFileName || "export") + "-" + new Date().toISOString().slice(0, 10);
    downloadCSV(name, columns, sorted);
  };

  // ── Styles ──
  const rowH = density === "compact" ? 32 : 40;
  const cellPad = density === "compact" ? "6px 12px" : "10px 14px";

  return (
    <>
      <style>{`
        .dt-wrap {
          background: #FFFFFF;
          border: 1px solid #EDEDED;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
          overflow: hidden;
          display: grid;
          grid-template-rows: 1fr auto;
        }
        .dt-scroller {
          overflow: auto;
          position: relative;
          max-height: 640px;
          scrollbar-width: thin;
        }
        .dt-scroller::-webkit-scrollbar { width: 8px; height: 8px; }
        .dt-scroller::-webkit-scrollbar-thumb { background: #DADCDF; border-radius: 4px; }
        .dt-scroller::-webkit-scrollbar-thumb:hover { background: #C4C9D0; }

        .dt-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-family: "Segoe UI", "Segoe UI Variable", system-ui, sans-serif;
          font-size: 13px;
          color: #232323;
        }
        .dt-thead {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #FAFAFA;
        }
        .dt-thead-scrolled th {
          box-shadow: 0 2px 4px rgba(15, 23, 42, 0.06);
        }
        .dt-thead-row th {
          padding: 8px 12px 4px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #484848;
          background: #FAFAFA;
          border-bottom: 1px solid #E1E1E1;
          white-space: nowrap;
          text-align: left;
          user-select: none;
        }
        .dt-th-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: color 0.12s;
        }
        .dt-th-inner:hover { color: #2e873d; }
        .dt-th-right { text-align: right; }
        .dt-th-right .dt-th-inner { flex-direction: row-reverse; }
        .dt-sort-arrow {
          color: #C4C9D0;
          font-size: 8px;
          line-height: 1;
        }
        .dt-sort-arrow.dt-sort-active { color: #2e873d; }

        .dt-filter-row th {
          padding: 4px 8px 8px;
          background: #FAFAFA;
          border-bottom: 1px solid #E1E1E1;
        }
        .dt-filter-input {
          width: 100%;
          height: 24px;
          padding: 2px 6px;
          font-family: inherit;
          font-size: 11.5px;
          color: #232323;
          background: #FFFFFF;
          border: 1px solid #E1E1E1;
          border-radius: 3px;
          outline: none;
          transition: all 0.12s;
        }
        .dt-filter-input:focus {
          border-color: #2e873d;
          box-shadow: 0 0 0 2px rgba(46, 135, 61, 0.10);
        }
        .dt-filter-input.dt-filter-active {
          border-bottom: 2px solid #2e873d;
          background: rgba(46, 135, 61, 0.04);
        }
        .dt-filter-spacer { width: 100%; height: 24px; }

        .dt-body-row {
          transition: background 0.08s;
          cursor: ${onRowClick ? "pointer" : "default"};
        }
        .dt-body-row:hover { background: rgba(46, 135, 61, 0.04); }
        .dt-body-row td {
          padding: ${cellPad};
          border-bottom: 1px solid #F2F2F2;
          height: ${rowH}px;
          line-height: 1.4;
          color: #232323;
          vertical-align: middle;
        }
        .dt-cell-right {
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .dt-cell-left { text-align: left; }

        /* Inline magnitude bar — sits BEHIND the value */
        .dt-bar-cell {
          position: relative;
          padding-right: 14px !important;
        }
        .dt-bar-cell::before {
          content: "";
          position: absolute;
          right: 14px; bottom: ${density === "compact" ? "4px" : "6px"};
          height: 2px;
          background: linear-gradient(to right, rgba(46, 135, 61, 0.0), rgba(46, 135, 61, 0.45));
          width: var(--bar-w, 0%);
          border-radius: 1px;
          pointer-events: none;
        }
        .dt-body-row:hover .dt-bar-cell::before {
          background: linear-gradient(to right, rgba(46, 135, 61, 0.0), rgba(46, 135, 61, 0.65));
        }

        .dt-empty {
          padding: 60px 20px;
          text-align: center;
          color: #9aa0a6;
          font-size: 13px;
          line-height: 1.6;
        }

        /* Footer */
        .dt-foot {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 8px 14px;
          background: #FAFAFA;
          border-top: 1px solid #EDEDED;
          font-size: 11.5px;
          color: #747474;
        }
        .dt-foot-count {
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: #484848;
        }
        .dt-foot-pulse { animation: dt-pulse 0.4s ease; }
        @keyframes dt-pulse {
          0% { color: #2e873d; }
          100% { color: #484848; }
        }
        .dt-foot-spacer { margin-left: auto; }
        .dt-foot-btn {
          background: transparent;
          border: 1px solid #E1E1E1;
          border-radius: 3px;
          padding: 3px 10px;
          font-family: inherit;
          font-size: 11.5px;
          color: #484848;
          cursor: pointer;
          transition: all 0.12s;
        }
        .dt-foot-btn:hover { border-color: #2e873d; color: #2e873d; background: rgba(46, 135, 61, 0.05); }
        .dt-foot-btn-primary {
          color: #2e873d;
          border-color: rgba(46, 135, 61, 0.4);
          background: rgba(46, 135, 61, 0.08);
        }
        .dt-foot-btn-primary:hover { background: #d9f4d7; }

        .dt-density-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0;
          border: 1px solid #E1E1E1;
          border-radius: 3px;
          overflow: hidden;
        }
        .dt-density-btn {
          background: transparent;
          border: none;
          padding: 3px 8px;
          font-family: inherit;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #747474;
          cursor: pointer;
          transition: all 0.12s;
        }
        .dt-density-btn:hover { color: #232323; background: #F0F0F0; }
        .dt-density-btn.dt-density-active {
          background: #d9f4d7;
          color: #2e873d;
        }

        .dt-show-all {
          background: rgba(46, 135, 61, 0.04);
          padding: 10px;
          text-align: center;
          font-size: 12px;
          color: #2e873d;
          font-weight: 600;
          border-top: 1px dashed #E1E1E1;
          cursor: pointer;
          transition: background 0.12s;
        }
        .dt-show-all:hover { background: rgba(46, 135, 61, 0.10); }
      `}</style>

      <div className="dt-wrap">
        <div className="dt-scroller" ref={scrollerRef} onScroll={onScroll}>
          <table className="dt-table">
            <thead className={"dt-thead " + (scrolled ? "dt-thead-scrolled" : "")}>
              <tr className="dt-thead-row">
                {columns.map((col) => {
                  const isSorted = sort?.key === col.key;
                  const arrow = !isSorted ? "▾" : sort!.dir === "desc" ? "▾" : "▴";
                  return (
                    <th
                      key={col.key}
                      className={col.align === "right" ? "dt-th-right" : ""}
                      style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                    >
                      <span className="dt-th-inner" onClick={() => toggleSort(col.key)}>
                        {col.label}
                        <span className={"dt-sort-arrow " + (isSorted ? "dt-sort-active" : "")}>
                          {arrow}
                        </span>
                      </span>
                    </th>
                  );
                })}
              </tr>
              <tr className="dt-filter-row">
                {columns.map((col) => (
                  <th key={col.key + "-f"}>
                    {col.filterable === false ? (
                      <div className="dt-filter-spacer" />
                    ) : (
                      <input
                        type="text"
                        className={
                          "dt-filter-input " +
                          (filters[col.key] && filters[col.key].trim() ? "dt-filter-active" : "")
                        }
                        placeholder="Filter…"
                        value={filters[col.key] || ""}
                        onChange={(e) => onFilterChange(col.key, e.target.value)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="dt-empty">{emptyMessage}</td>
                </tr>
              ) : (
                visible.map((row, i) => (
                  <tr
                    key={i}
                    className="dt-body-row"
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => {
                      const align = col.align || (col.format && col.format !== "text" && col.format !== "date" ? "right" : "left");
                      const cls = (align === "right" ? "dt-cell-right" : "dt-cell-left") + (col.showBar ? " dt-bar-cell" : "");
                      const raw = row[col.key];
                      let style: React.CSSProperties | undefined;
                      if (col.showBar) {
                        const max = columnMax[col.key] || 1;
                        const v = Math.abs(Number(raw) || 0);
                        const pct = Math.min(100, Math.round((v / max) * 100));
                        style = { ["--bar-w" as any]: pct + "%" } as React.CSSProperties;
                      }
                      return (
                        <td key={col.key} className={cls} style={style}>
                          {col.render ? col.render(row) : fmtCell(raw, col)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {initialRowLimit != null && !showAll && sorted.length > initialRowLimit && (
            <div className="dt-show-all" onClick={() => setShowAll(true)}>
              Show all {sorted.length.toLocaleString("en-AU")} rows
              <Icon iconName="ChevronDown" style={{ fontSize: 10, marginLeft: 4 }} />
            </div>
          )}
        </div>

        {showFooter && (
          <div className="dt-foot">
            <span>
              <span className="dt-foot-count" key={filtered.length}>
                {filtered.length.toLocaleString("en-AU")}
              </span>
              {filtered.length !== rows.length && (
                <>
                  {" "}of <span className="dt-foot-count">{rows.length.toLocaleString("en-AU")}</span> rows
                </>
              )}
              {filtered.length === rows.length && (
                <> {filtered.length === 1 ? "row" : "rows"}</>
              )}
            </span>

            {activeFilterCount > 0 && (
              <button className="dt-foot-btn dt-foot-btn-primary" onClick={clearFilters}>
                Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
              </button>
            )}

            <span className="dt-foot-spacer" />

            <span className="dt-density-toggle" role="group" aria-label="Row density">
              <button
                className={"dt-density-btn " + (density === "compact" ? "dt-density-active" : "")}
                onClick={() => setDensity("compact")}
                title="Compact rows"
              >
                Compact
              </button>
              <button
                className={"dt-density-btn " + (density === "comfortable" ? "dt-density-active" : "")}
                onClick={() => setDensity("comfortable")}
                title="Comfortable rows"
              >
                Comfy
              </button>
            </span>

            {exportFileName && (
              <button className="dt-foot-btn" onClick={doExport} title="Download CSV of the current view">
                <Icon iconName="Download" style={{ fontSize: 11, marginRight: 4 }} />
                CSV
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
